import React, { useState, useEffect } from 'react';
import PaymentModal from './PaymentModal.tsx';
import { AppIcon, invoiceTypeIcon } from '../../icons/appIcons.tsx';
import EmptyState from '../common/EmptyState.tsx';

interface Invoice {
  id: string;
  invoiceNumber: string;
  title: string;
  description: string;
  amount: string;
  paidAmount: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue';
  type: 'membership' | 'lesson' | 'event' | 'equipment' | 'other';
  dueDate: string;
  createdAt: string;
  paidAt?: string;
  isRecurring?: boolean;
  recurringFrequency?: 'monthly' | 'quarterly' | 'annually';
}

interface AlumniInvoicesProps {
  compact?: boolean;
}

export default function AlumniInvoices({ compact = false }: AlumniInvoicesProps) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [payingInvoice, setPayingInvoice] = useState<Invoice | null>(null);
  const [showAllInvoices, setShowAllInvoices] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async (retryCount = 0) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/invoices', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 429 && retryCount < 3) {
        // Retry with exponential backoff
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        console.log(`Rate limited, retrying invoices in ${delay}ms...`);
        setTimeout(() => fetchInvoices(retryCount + 1), delay);
        return;
      }

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Failed to parse invoices response:', parseError);
        return;
      }
      if (data.success) {
        // Filter invoices to only show visible ones for alumni
        let filteredInvoices = data.data;
        
        // Check user role from localStorage or context
        const userRole = JSON.parse(localStorage.getItem('authUser') || '{}').role;
        
        // For alumni users, only show invoices that are visible
        if (userRole === 'alumni') {
          const now = new Date();
          filteredInvoices = data.data.filter(invoice => {
            if (invoice.visibleDate) {
              return new Date(invoice.visibleDate) <= now;
            }
            return true; // Show invoices without visibleDate (legacy)
          });
        }
        
        setInvoices(filteredInvoices);
      } else {
        console.error('Failed to fetch invoices:', data.message);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      if (retryCount === 0) { // Only set loading false on first attempt
        setLoading(false);
      }
    }
  };

  const initiatePayment = async (invoice: Invoice) => {
    setPayingInvoice(invoice);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800 border-green-200';
      case 'sent': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'overdue': return 'bg-red-100 text-red-800 border-red-200';
      case 'draft': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatCurrency = (amount: string) => {
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isOverdue = (dueDate: string, status: string) => {
    if (status === 'paid') return false;
    return new Date(dueDate) < new Date();
  };

  const unpaidInvoices = invoices.filter(inv => inv.status !== 'paid');
  const displayInvoices = compact 
    ? unpaidInvoices.slice(0, showAllInvoices ? undefined : 3)
    : showAllInvoices 
      ? invoices 
      : unpaidInvoices;

  if (loading) {
    return (
      <div className={compact ? "animate-pulse" : "px-12 py-10"}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <>
        <div className="flex justify-end items-center mb-4">
          <span className="text-sm text-gray-600">
            {unpaidInvoices.length} unpaid
          </span>
        </div>
        
        <div className="space-y-4">
          {displayInvoices.length === 0 ? (
            <div className="bg-gradient-to-br from-green-50 to-white rounded-xl border border-green-200 shadow-sm">
              <EmptyState icon="emptyMoney" title="All Caught Up!" description="All invoices are paid!" className="py-8" />
            </div>
          ) : (
            displayInvoices.map(invoice => (
              <div 
                key={invoice.id} 
                className={`group relative bg-white rounded-xl border-2 shadow-md hover:shadow-xl transition-all duration-200 overflow-hidden ${
                  isOverdue(invoice.dueDate, invoice.status) 
                    ? 'border-red-300 hover:border-red-400' 
                    : invoice.status === 'sent'
                    ? 'border-blue-300 hover:border-blue-400'
                    : invoice.status === 'paid'
                    ? 'border-green-300 hover:border-green-400'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* Status indicator bar */}
                <div className={`absolute top-0 left-0 right-0 h-1.5 ${
                  isOverdue(invoice.dueDate, invoice.status)
                    ? 'bg-gradient-to-r from-red-400 to-red-600'
                    : invoice.status === 'sent'
                    ? 'bg-gradient-to-r from-blue-400 to-blue-600'
                    : invoice.status === 'paid'
                    ? 'bg-gradient-to-r from-green-400 to-green-600'
                    : 'bg-gradient-to-r from-gray-300 to-gray-400'
                }`} />
                
                <div className="p-4 pt-5">
                  <div className="flex justify-between items-start gap-4">
                    {/* Left side - Invoice info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3 mb-3">
                        <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-xl shadow-sm ${
                          isOverdue(invoice.dueDate, invoice.status)
                            ? 'bg-red-100'
                            : invoice.status === 'sent'
                            ? 'bg-blue-100'
                            : invoice.status === 'paid'
                            ? 'bg-green-100'
                            : 'bg-gray-100'
                        }`}>
                          <AppIcon name={invoiceTypeIcon(invoice.type)} className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 text-base mb-1 truncate">{invoice.title}</h4>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(invoice.status)}`}>
                              {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                            </span>
                            <span className="text-xs text-gray-500">#{invoice.invoiceNumber}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className={`flex items-center gap-1.5 text-sm ${
                        isOverdue(invoice.dueDate, invoice.status)
                          ? 'text-red-700 font-semibold'
                          : 'text-gray-600'
                      }`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>Due: {formatDate(invoice.dueDate)}</span>
                        {isOverdue(invoice.dueDate, invoice.status) && (
                          <span className="ml-1 text-xs font-semibold">(OVERDUE)</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Right side - Amount and action */}
                    <div className="text-right flex-shrink-0">
                      <div className={`text-2xl font-bold mb-2 ${
                        isOverdue(invoice.dueDate, invoice.status)
                          ? 'text-red-600'
                          : invoice.status === 'paid'
                          ? 'text-green-600'
                          : 'text-gray-900'
                      }`}>
                        {formatCurrency(invoice.amount)}
                      </div>
                      {invoice.status === 'paid' ? (
                        <div className="inline-flex items-center gap-1.5 text-sm font-medium text-green-600">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Paid
                        </div>
                      ) : (
                        <button
                          onClick={() => initiatePayment(invoice)}
                          disabled={payingInvoice?.id === invoice.id}
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed ${
                            isOverdue(invoice.dueDate, invoice.status)
                              ? 'bg-red-600 hover:bg-red-700 text-white'
                              : 'bg-ggaa-green hover:bg-ggaa-green/90 text-white'
                          }`}
                        >
                          {payingInvoice?.id === invoice.id ? (
                            <>
                              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span>Processing...</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                              </svg>
                              <span>Pay Now</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Payment Modal */}
        {payingInvoice && (
          <PaymentModal
            invoice={payingInvoice}
            onClose={() => setPayingInvoice(null)}
            onSuccess={() => {
              setPayingInvoice(null);
              fetchInvoices(); // Refresh invoices after payment
            }}
          />
        )}
      </>
    );
  }

  // Full view (not compact)
  return (
    <div className="px-12 py-10">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Invoices & Payments</h1>
          <p className="text-gray-600 mt-2">View and pay your invoices</p>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-600">Outstanding Balance</div>
          <div className="text-2xl font-bold text-red-600">
            {formatCurrency(
              unpaidInvoices.reduce((sum, inv) => sum + (parseFloat(inv.amount) - parseFloat(inv.paidAmount)), 0).toString()
            )}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-2xl font-bold text-gray-900">{invoices.length}</div>
          <div className="text-sm text-gray-600">Total Invoices</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-2xl font-bold text-red-600">{unpaidInvoices.length}</div>
          <div className="text-sm text-gray-600">Unpaid Invoices</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-2xl font-bold text-green-600">
            {formatCurrency(
              invoices.filter(inv => inv.status === 'paid')
                .reduce((sum, inv) => sum + parseFloat(inv.amount), 0).toString()
            )}
          </div>
          <div className="text-sm text-gray-600">Total Paid</div>
        </div>
      </div>

      {/* Filter Toggle */}
      <div className="mb-6">
        <button
          onClick={() => setShowAllInvoices(!showAllInvoices)}
          className="bg-ggaa-green text-white px-4 py-2 rounded-lg hover:bg-ggaa-green/90 font-medium"
        >
          {showAllInvoices ? 'Show only unpaid invoices' : 'Show all invoices'}
        </button>
      </div>

      {/* Invoices List */}
      <div className="space-y-4">
        {displayInvoices.length === 0 ? (
          <div className="bg-white rounded-lg shadow border border-gray-200">
            <EmptyState icon="emptyMoney" title="All caught up!" description="You have no outstanding invoices." className="py-12" />
          </div>
        ) : (
          displayInvoices.map(invoice => (
            <div 
              key={invoice.id} 
              className={`bg-white rounded-lg shadow border p-6 ${
                isOverdue(invoice.dueDate, invoice.status) 
                  ? 'border-red-200 bg-red-50' 
                  : 'border-gray-200'
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <AppIcon name={invoiceTypeIcon(invoice.type)} className="w-6 h-6 shrink-0" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{invoice.title}</h3>
                      <p className="text-sm text-gray-600">Invoice #{invoice.invoiceNumber}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm border font-medium ${getStatusColor(invoice.status)}`}>
                      {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                    </span>
                  </div>
                  
                  {invoice.description && (
                    <p className="text-gray-600 mb-3">{invoice.description}</p>
                  )}
                  
                  <div className="flex items-center space-x-6 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Due Date:</span> {formatDate(invoice.dueDate)}
                    </div>
                    <div>
                      <span className="font-medium">Type:</span> {invoice.type.charAt(0).toUpperCase() + invoice.type.slice(1)}
                    </div>
                    {invoice.isRecurring && (
                      <div>
                        <span className="font-medium">Recurring:</span> {invoice.recurringFrequency}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900 mb-2">
                    {formatCurrency(invoice.amount)}
                  </div>
                  {parseFloat(invoice.paidAmount) > 0 && (
                    <div className="text-sm text-green-600 mb-2">
                      Paid: {formatCurrency(invoice.paidAmount)}
                    </div>
                  )}
                  {invoice.status !== 'paid' && (
                    <button
                      onClick={() => initiatePayment(invoice)}
                      disabled={payingInvoice?.id === invoice.id}
                      className="mt-2 bg-ggaa-green text-white px-4 py-2 rounded-lg hover:bg-ggaa-green/90 disabled:opacity-50 font-medium"
                    >
                      {payingInvoice?.id === invoice.id ? 'Processing...' : 'Pay Now'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Payment Modal */}
      {payingInvoice && (
        <PaymentModal
          invoice={payingInvoice}
          onClose={() => setPayingInvoice(null)}
          onSuccess={() => {
            setPayingInvoice(null);
            fetchInvoices(); // Refresh invoices after payment
          }}
        />
      )}
    </div>
  );
}

